import { type VoucherCopyState } from '@/components/common/voucher-ticket';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Long enough to read "Đã chép", short enough not to sit there. */
const COPY_FEEDBACK_MS = 1800;

/** Errors need longer — the user has to notice and fall back to selecting the code. */
const ERROR_FEEDBACK_MS = 2500;

interface UseVoucherCopyOptions {
  /** Fired after the code lands on the clipboard, before the state resets. */
  onCopied?: () => void;
}

interface UseVoucherCopyReturn {
  copyState: VoucherCopyState;
  copy: (code: string) => Promise<void>;
}

/**
 * Clipboard state for a voucher code, shaped to feed `VoucherTicket` /
 * `VoucherChip`'s `copyState` prop directly. One instance drives one code at a
 * time — a rail of tickets tracks which one is active alongside this.
 */
export function useVoucherCopy(options?: UseVoucherCopyOptions): UseVoucherCopyReturn {
  const [copyState, setCopyState] = useState<VoucherCopyState>('idle');
  const resetTimer = useRef<number | null>(null);
  // Kept in a ref so `copy` stays referentially stable across renders — callers
  // pass it straight into `onCopy` props, and a new identity every render would
  // churn every memoised child in the rail.
  const onCopied = useRef(options?.onCopied);
  onCopied.current = options?.onCopied;

  const clearResetTimer = useCallback(() => {
    if (resetTimer.current !== null) {
      window.clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }
  }, []);

  useEffect(() => clearResetTimer, [clearResetTimer]);

  const copy = useCallback(
    async (code: string) => {
      clearResetTimer();
      setCopyState('copying');

      try {
        await navigator.clipboard.writeText(code);
        setCopyState('copied');
        onCopied.current?.();
        resetTimer.current = window.setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS);
      } catch (error) {
        console.error('Failed to copy voucher code:', error);
        setCopyState('error');
        resetTimer.current = window.setTimeout(() => setCopyState('idle'), ERROR_FEEDBACK_MS);
      }
    },
    [clearResetTimer],
  );

  return { copyState, copy };
}
